import App from 'antd/lib/app';
import { Modal } from 'antd/lib';
import Form from 'antd/lib/form';
import Button from 'antd/lib/button';
import Input from 'antd/lib/input';
import React from 'react';
import Typography from 'antd/lib/typography';
import DatePicker from 'antd/lib/date-picker';
import dayjs from 'dayjs';

import { PjmSeasonDto, UpdatePjmSeasonDto } from '../../../../../domain/pjm/season/PjmSeasonDto';
import { usePjmSeasonUpdateMutation } from '../../../../../domain/pjm/season/queries';
import { isDateInSeasonRange } from '../../../../../domain/pjm/season/isDateInSeasonRange';
import { isEqual } from 'date-fns/isEqual';

import './PjmSeasonModal.css';

interface IPjmSeasonModalProps {
    seasonId: string;
    seasonData?: PjmSeasonDto | null;
    onClose: (actionTaken: boolean) => void;
}

interface IPjmSeason {
    id: string;
    peak_load_day_1: Date;
    peak_load_day_2: Date;
    peak_load_day_3: Date;
    peak_load_day_4: Date;
    peak_load_day_5: Date;
}

const PEAK_DAY_FIELDS = ['peak_load_day_1', 'peak_load_day_2', 'peak_load_day_3', 'peak_load_day_4', 'peak_load_day_5'];

export const PjmSeasonModal = ({ seasonId, seasonData, onClose }: IPjmSeasonModalProps) => {
    const { notification } = App.useApp();
    const [form] = Form.useForm<IPjmSeason>(); // form should have 5 days

    const { mutateAsync: savePjmSeason, isLoading } = usePjmSeasonUpdateMutation();

    const toUtcMidnightIso = (value: Date | dayjs.Dayjs) => dayjs(value).utc(true).startOf('day').toISOString();

    const onFormFinish = async (seasonData: IPjmSeason) => {
        const preparedSeasonData: UpdatePjmSeasonDto = {
            id: seasonData.id,
            peakDays: [
                toUtcMidnightIso(seasonData.peak_load_day_1),
                toUtcMidnightIso(seasonData.peak_load_day_2),
                toUtcMidnightIso(seasonData.peak_load_day_3),
                toUtcMidnightIso(seasonData.peak_load_day_4),
                toUtcMidnightIso(seasonData.peak_load_day_5),
            ],
        };

        try {
            await savePjmSeason(preparedSeasonData);

            notification.success({ key: makeKey('save-notification'), message: 'PJM Season saved successfully!' });
            onClose(true);
        } catch (error: any) {
            notification.error({ key: makeKey('save-notification-error'), message: (error as Error)?.message || 'Cannot save PJM Season!' });
        }
    };

    const dayValidator = (itemName: string) => (_: any, value: Date) => {
        const [endYear, startYear] = seasonId.split('-').reverse().map(Number);

        // Check season
        if (!isDateInSeasonRange(seasonId, value)) {
            return Promise.reject(new Error(`Date must be within the season range for winter ${startYear - 2}-${endYear - 2}`));
        }

        // Check uniqueness
        const otherDays: Date[] = PEAK_DAY_FIELDS.filter(key => key !== itemName).map(key => form.getFieldValue(key as keyof IPjmSeason)) as Date[];

        const duplicate = otherDays.some(d => isEqual(d, value));

        if (duplicate) {
            return Promise.reject(new Error('Each Peak Load Day must be unique'));
        }

        return Promise.resolve();
    };

    const onDateChange = () => {
        const touchedFields = PEAK_DAY_FIELDS.filter(key => form.isFieldTouched(key as keyof IPjmSeason));

        form.validateFields(touchedFields);
    };

    return (
        <Modal
            title={makeTitle(seasonId)}
            className="pjm-season-modal"
            open
            width={600}
            styles={{ header: { marginRight: '20px' }, body: { paddingLeft: '50px' } }}
            destroyOnHidden
            maskClosable={false}
            keyboard={false}
            onCancel={() => onClose(false)}
            footer={[
                <Button key={makeKey('cancel')} onClick={() => onClose(false)}>
                    Cancel
                </Button>,
                <Button key={makeKey('save')} type="primary" loading={isLoading} onClick={form.submit}>
                    Save
                </Button>,
            ]}
        >
            <Form
                form={form}
                name="pjm-season-form"
                className="pjm-season-form-label"
                preserve={false}
                layout="horizontal"
                onFinish={onFormFinish}
                initialValues={transformSeasonDataToInitialValues({ id: seasonId, ...seasonData })}
            >
                <Form.Item name="id" hidden>
                    <Input />
                </Form.Item>
                <Form.Item
                    name="peak_load_day_1"
                    required
                    rules={[{ required: true, message: 'Please select date!' }, { validator: dayValidator('peak_load_day_1') }]}
                    label={<Typography.Text strong>Peak Load Day 1&nbsp;</Typography.Text>}
                >
                    <DatePicker data-cy="date-picker" format="DD/MMM/YYYY" size="large" allowClear={false} onChange={onDateChange} />
                </Form.Item>
                <Form.Item
                    name="peak_load_day_2"
                    required
                    rules={[{ required: true, message: 'Please select date!' }, { validator: dayValidator('peak_load_day_2') }]}
                    label={<Typography.Text strong>Peak Load Day 2&nbsp;</Typography.Text>}
                >
                    <DatePicker data-cy="date-picker" format="DD/MMM/YYYY" size="large" allowClear={false} onChange={onDateChange} />
                </Form.Item>
                <Form.Item
                    name="peak_load_day_3"
                    required
                    rules={[{ required: true, message: 'Please select date!' }, { validator: dayValidator('peak_load_day_3') }]}
                    label={<Typography.Text strong>Peak Load Day 3&nbsp;</Typography.Text>}
                >
                    <DatePicker data-cy="date-picker" format="DD/MMM/YYYY" size="large" allowClear={false} onChange={onDateChange} />
                </Form.Item>
                <Form.Item
                    name="peak_load_day_4"
                    required
                    rules={[{ required: true, message: 'Please select date!' }, { validator: dayValidator('peak_load_day_4') }]}
                    label={<Typography.Text strong>Peak Load Day 4&nbsp;</Typography.Text>}
                >
                    <DatePicker data-cy="date-picker" format="DD/MMM/YYYY" size="large" allowClear={false} onChange={onDateChange} />
                </Form.Item>
                <Form.Item
                    name="peak_load_day_5"
                    required
                    rules={[{ required: true, message: 'Please select date!' }, { validator: dayValidator('peak_load_day_5') }]}
                    label={<Typography.Text strong>Peak Load Day 5&nbsp;</Typography.Text>}
                >
                    <DatePicker data-cy="date-picker" format="DD/MMM/YYYY" size="large" allowClear={false} onChange={onDateChange} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

function makeKey(label: string) {
    return `pjm-season-modal-${label}`;
}

function makeTitle(seasonId: string) {
    const [endYear, startYear] = seasonId.split('-').reverse().map(Number);

    return `Peak Load Days of ${startYear - 2}-${endYear - 2} season for WPL calculation ${startYear}-${endYear}`;
}

function transformSeasonDataToInitialValues(dto: Partial<PjmSeasonDto>) {
    const [peakLoadDay1, peakLoadDay2, peakLoadDay3, peakLoadDay4, peakLoadDay5] = dto.peakDays || [];

    return {
        id: dto.id,
        peak_load_day_1: peakLoadDay1 ? dayjs.utc(peakLoadDay1) : null,
        peak_load_day_2: peakLoadDay2 ? dayjs.utc(peakLoadDay2) : null,
        peak_load_day_3: peakLoadDay3 ? dayjs.utc(peakLoadDay3) : null,
        peak_load_day_4: peakLoadDay4 ? dayjs.utc(peakLoadDay4) : null,
        peak_load_day_5: peakLoadDay5 ? dayjs.utc(peakLoadDay5) : null,
    };
}
